import { FC, ChangeEvent, useContext, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';

// Contexts
import { LanguageContext } from '../../../core/TenantProvider/contexts';
// Components - Atoms, Molecules, Organisms, Pages
import AEText from '../../atoms/AEText';
import AEValidationErrorMessage from '../../atoms/AEValidationErrorMessage';
import APButton from '../../atoms/APButton';
// Types
import { HexColour } from '../../../core/types/ThemeConfigTypes';
import { CustomFile } from '../../../core/types/GeneralTypes';
import { TranslateFunction } from '../../../core/types/TypographyTypes';
// Utils
import TranslateWrapper from '../../../core/utils/TranslateWrapper';



const Container = styled(Box)(({ theme }) => {
  const {
    colours: {
      borders: { borderInput, borderInputActive, borderInputFocus },
      backgrounds: { backgroundAlt, backgroundAltInverseWeak }
    }
  } = theme;

  const COLOUR_BORDER_BORDER_INPUT: HexColour = borderInput;
  const COLOUR_BORDER_BORDER_INPUT_ACTIVE: HexColour = borderInputActive;
  const COLOUR_BORDER_BORDER_INPUT_FOCUS: HexColour = borderInputFocus;
  const COLOUR_BG_BACKGROUND_ALT: HexColour = backgroundAlt;
  const COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK: HexColour = backgroundAltInverseWeak;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    backgroundColor: COLOUR_BG_BACKGROUND_ALT,
    border: `1px solid ${COLOUR_BORDER_BORDER_INPUT}`,
    borderRadius: '8px',
    padding: '20px 20px 18px 20px',

    '&:hover, &:active': {
      border: `1px solid ${COLOUR_BORDER_BORDER_INPUT_ACTIVE}`,
      backgroundColor: COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK
    },

    ':focus-within': {
      border: `1px solid ${COLOUR_BORDER_BORDER_INPUT_FOCUS}`,
      backgroundColor: COLOUR_BG_BACKGROUND_ALT_INVERSE_WEAK
    }
  };
});

const FileInput = styled('input')(() => {
  return {
    display: 'none'
  };
});

const BrowseFileButton = styled(APButton)(() => {
  return {
    width: '150px'
  };
});

const UploadFileCopySection = styled(Box)(() => {
  return {
    textAlign: 'center'
  };
});

interface APUploadFileSectionProps {
  handleFileUpload: Function;
  value: CustomFile | null;
  availableFileTypesText?: string;
  maxFileSizeText?: string;
  error?: string;
}

const APUploadFileSection: FC<APUploadFileSectionProps> = (props: APUploadFileSectionProps) => {
  const hiddenFileInput: React.RefObject<HTMLInputElement | null>  = useRef<HTMLInputElement | null>(null);

  const { translations } = useContext(LanguageContext);
  const translate: TranslateFunction = TranslateWrapper(translations);

  const browseFileBtnText: string = translate('documentUpload.browseFileBtnText');
  const fileUploadLine1Text: string = translate('documentUpload.fileUploadLine1');
  const fileUploadLine2Text: string = translate('documentUpload.fileUploadLine2');

  const { handleFileUpload, value, availableFileTypesText, maxFileSizeText, error } = props;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event && event.target && event.target.files && handleFileUpload(event.target.files);
  };

  const uploadFile = () => {
    hiddenFileInput && hiddenFileInput.current && hiddenFileInput?.current.click();
  };

  return (
    <Container onClick={uploadFile} data-testid="ae-file-upload-section">
      <FileInput
        type="file"
        ref={hiddenFileInput}
        onChange={handleChange}
        data-testid="ae-file-upload-input"
        value={value ? value.name : ''}
        id="document-upload"
      />
      <BrowseFileButton
        color='primary'
        variant='contained'
        disableHover={true}
        name="browseFileButton"
        data-testid="ae-button-browse-file"
        id="document-upload-button"
      >
        {browseFileBtnText}
      </BrowseFileButton>
      <UploadFileCopySection>
        <AEText
          variant="subtitle1"
          type="normal"
          text={availableFileTypesText ? availableFileTypesText : fileUploadLine1Text}
        />
        <AEText variant="subtitle1" type="normal" text={maxFileSizeText ? maxFileSizeText : fileUploadLine2Text} />
        {!!error && <AEValidationErrorMessage message={error} />}
      </UploadFileCopySection>
    </Container>
  );
};

export default APUploadFileSection;
